/* eslint-disable ember/no-computed-properties-in-native-classes */
import Controller from '@ember/controller';
import { action, computed, set } from '@ember/object';
import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { IconFlagCode } from '@mylighthouse/prism-ember/types/components/prism/icon-flag';
import Countries from 'frontend-utils/utils/countries';
import cloneDeep from 'lodash.clonedeep';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { RS_POS_COUNTRIES } from 'lumen/utils/constants/rate-strategy-constants';

interface PosItem {
  countries: {
    id: string;
    name: string;
  }[];
  id?: IconFlagCode;
  name: string;
  order: number;
}

export default class HotelSettingsPosController extends Controller {
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked footerElement: Element | null = null;

  // temp object holding the changes made by the user before saving
  @tracked rsPosCountries: PosItem[] = [
    {
      name: '',
      order: 1,
      countries: [],
    },
    {
      name: '',
      order: 2,
      countries: [],
    },
    {
      name: '',
      order: 3,
      countries: [],
    },
  ];

  private countriesUtil: Countries = new Countries(getOwner(this)!);

  // hold the RS POS countries returned by the backend (ground truth)
  prevPosCountries: PosItem[] = [
    {
      name: '',
      order: 1,
      countries: [],
    },
    {
      name: '',
      order: 2,
      countries: [],
    },
    {
      name: '',
      order: 3,
      countries: [],
    },
  ];

  // getter to get the list of all countries that support POS
  @computed(
    'countries.[]',
    'hotelAndMarketService.hotel.rsPosCountryOptions.[]',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableCountries() {
    const supportedCountries =
      this.hotelAndMarketService.hotel.rsPosCountryOptions;

    // filter out the countries that support RS POS and convert their IDs to lowercase
    const availableCountriesTemp = this.countries
      .filter((country) => supportedCountries.includes(country.id))
      .map((filteredCountry) => ({
        id: filteredCountry.id.toLowerCase(),
        name: filteredCountry.name,
      })) as { id: string; name: string }[];

    // add None option used for clearance of a selection
    availableCountriesTemp.unshift({
      id: '',
      name: `${this.intl.t('settings.pos.none')}`,
    });

    if (
      !supportedCountries ||
      supportedCountries.length === 0 ||
      availableCountriesTemp.length === 0
    ) {
      this.errorTracking.captureMessage('No POS countries in settings', {
        supportedCountries,
        countries: this.countries,
        availableCountriesTemp,
        hotel: this.hotelAndMarketService.hotel,
      });
    }

    return availableCountriesTemp;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get countries() {
    return this.countriesUtil.list;
  }

  // Save is disabled if pos settings are not changed or if user sets all pos options to none
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSaveDisabled() {
    const orderOrCountryChanged = this.rsPosCountries.some(
      (country, index) =>
        country.id !== this.prevPosCountries[index]?.id ||
        country.order !== this.prevPosCountries[index]?.order,
    );
    const noCountrySelected = this.rsPosCountries.every(
      (country) => country.id === undefined,
    );
    return !orderOrCountryChanged || noCountrySelected;
  }

  @computed('hotelAndMarketService.hotel.rsDefaultPOS')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rsDefaultPOS() {
    return {
      id: this.hotelAndMarketService.hotel.rsDefaultPOS.toLowerCase() as IconFlagCode,
      name: this.fetchCountryName(
        this.hotelAndMarketService.hotel.rsDefaultPOS,
      ),
    };
  }

  // correct the order of the countries so that they are consecutive to one another

  static correctOrder(countries: { order: number }[]): void {
    countries.forEach((country, order) => {
      set(country, 'order', order + 1);
    });
  }

  // remove the already selected countries from the other dropdown lists

  correctOtherAvailableCountries(): void {
    const selectedCountriesIds = this.rsPosCountries
      .filter((country) => country.id !== undefined)
      .map(({ id }) => id);

    // filter out the selected countries from the available countries
    const remainingAvailableCountries = this.availableCountries.filter(
      (availableCountry) =>
        !selectedCountriesIds.includes(availableCountry.id as IconFlagCode),
    );
    this.rsPosCountries.forEach((country) =>
      set(country, 'countries', remainingAvailableCountries),
    );
  }

  // return the name of the country given its ID
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchCountryName(id: string) {
    return this.countries.filter(
      (country) => country.id.toUpperCase() === id,
    )[0]?.name;
  }

  // push the selected countries to top such that they are on consecutive ranks when saved
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  pushToTop(rsPosCountries: PosItem[]) {
    // filter out empty ones, aka here no country is selected
    const rsPosCountriesTemp = rsPosCountries.filter(
      (country) => country.id !== undefined,
    );

    // backfill so we end up with an array of 3 countries
    const additionalCountryAmount =
      RS_POS_COUNTRIES - rsPosCountriesTemp.length;
    for (let i = 0; i < additionalCountryAmount; i++) {
      rsPosCountriesTemp.push({
        name: '',
        order: 0,
        countries: this.availableCountries,
      });
    }

    // reset the order
    HotelSettingsPosController.correctOrder(rsPosCountriesTemp);

    return rsPosCountriesTemp;
  }

  savePosCountries(): void {
    this.rsPosCountries = this.pushToTop(this.rsPosCountries);

    // reset the available countries such that other dropdown lists do not contain the selected countries
    this.correctOtherAvailableCountries();

    // update the ground truth, aka prev pos countries
    this.prevPosCountries = cloneDeep(this.rsPosCountries);

    const { hotel } = this.hotelAndMarketService;
    // Make all IDs uppercase and filter the empty ones to make it consistent with backend
    set(
      hotel,
      'rsPosCountries',
      this.rsPosCountries
        .map((country) => country.id)
        .filter((id) => id !== undefined)
        .map((id) => id.toUpperCase()),
    );

    try {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      hotel.save();
      this.usageAnalytics.track('POS Settings Updated', {
        eventTrigger: 'settings-button',
      });
      this.toast.success(this.intl.t('general.changes_saved'), {
        showIcon: true,
      });
    } catch {
      this.toast.alert(this.intl.t('general.error_short'), { showIcon: true });
    }
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  cancelPosCountries() {
    // return back to ground truth
    this.rsPosCountries = cloneDeep(this.prevPosCountries);
  }

  @action confirmChangesOnSave(): void {
    this.savePosCountries();
  }

  @action onInsertFooter(): void {
    this.footerElement = document.querySelector('#pos-save-container');
  }

  @action reorderCountries(countries: PosItem[]): void {
    HotelSettingsPosController.correctOrder(countries);
    this.rsPosCountries = countries;
  }

  @action setCurrentCountry(selectedPos: PosItem, args: PosItem): void {
    set(selectedPos, 'id', args.id as IconFlagCode);
    set(selectedPos, 'name', args.name);
    this.correctOtherAvailableCountries();

    this.rsPosCountries = cloneDeep(this.rsPosCountries);
  }

  // Set prevPosCountries with the rsPosCountries returned by the backend
  @action setRsPosCountries(): void {
    this.hotelAndMarketService.hotel.rsPosCountries.forEach(
      (countryId, index) => {
        const country = this.rsPosCountries[index];

        if (country) {
          country.id = countryId.toLowerCase() as IconFlagCode;
          country.name = this.fetchCountryName(countryId) ?? '';
        }
      },
    );

    this.correctOtherAvailableCountries();
    // update the entire tracked object
    this.rsPosCountries = cloneDeep(this.rsPosCountries);
    // update the ground truth
    this.prevPosCountries = cloneDeep(this.rsPosCountries);
  }
}
